import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Heart,
  HeartHandshake,
  Minus,
  Plus,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Sunrise,
  Truck,
  UtensilsCrossed,
} from "lucide-react";
import CTAButton from "@/components/CTAButton";
import Reveal from "@/components/Reveal";
import Marquee, { NoMarquee } from "@/components/Marquee";


import WordRotator from "@/components/WordRotator";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import { homeCopy, faqs, howItWorksDetailed } from "@/data/content";
import { menuWeeks } from "@/data/menu";
import { breakfastItems } from "@/data/breakfast";
import { allPlans } from "@/lib/plans";
import { useBrand } from "@/context/BrandContext";
import media from "@/config/media";

const trustItems = [
  "10 breakfasts + 15 lunches",
  "Portions for ages 2 to 11+",
  "Ingredients listed on every dish",
  "Organic jaggery, not refined sugar",
  "Cooked in A2 cow ghee",
  "Developed with paediatric nutritionists",
  "100% plastic-free packaging",
  "A parent note with every meal",
  "Home or school delivery in Pune",
];

const noList = [
  "artificial colours",
  "refined white sugar",
  "packet masalas",
  "pre-mixes",
  "shelf-stable packets",
  "reheated leftovers",
  "hidden allergens",
  "plastic packaging",
];

const collections = [
  {
    id: "breakfast",
    stage: "Every school morning",
    title: "Breakfast",
    subtitle: `${breakfastItems.length} dishes`,
    copy: "Regional Indian porridges, steamed fruit, sprouted millets and warm milk bowls.",
    image: media.breakfast.bajraKadha,
    to: "/meals?tab=breakfast",
    filter: "breakfast",
  },
  {
    id: "lunch",
    stage: "Ages 2 to 11+",
    title: "Lunch",
    subtitle: "15 dishes",
    copy: "A three-week rotation of familiar Indian lunches, so no week repeats itself.",
    image: media.meals.varanBhaat,
    to: "/meals?tab=week-1",
    filter: "lunch",
  },
  {
    id: "combined",
    stage: "Both meals covered",
    title: "Breakfast + Lunch",
    subtitle: "10 meals a week",
    copy: "The full day handled — mornings and midday, on one plan and one delivery.",
    image: media.meals.khichdiKadhi,
    to: "/plans",
    filter: "both",
  },
  {
    id: "weekly",
    stage: "Try it first",
    title: "Weekly plan",
    subtitle: "1 week",
    copy: "One week of whichever meals you choose. The simplest way to start.",
    image: media.meals.ragiIdli,
    to: "/plans",
    filter: "both",
  },
  {
    id: "monthly",
    stage: "Best value",
    title: "Monthly plan",
    subtitle: "Full 3-week cycle",
    copy: "The complete rotation, so your child never eats the same meal twice in a cycle.",
    image: media.meals.puranPoli,
    to: "/plans",
    filter: "both",
  },
  {
    id: "school",
    stage: "For school days",
    title: "School delivery",
    subtitle: "Selected schools",
    copy: "Delivered to the school gate at the agreed time, or to your door at home.",
    image: media.childEating,
    to: "/delivery-areas",
    filter: "both",
  },
];

const filters = [
  { id: "all", label: "All" },
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch", label: "Lunch" },
  { id: "both", label: "Plans" },
];

const differentiators = [
  {
    id: "breakfast",
    eyebrow: "Our breakfasts",
    title: "The mornings our grandmothers got right",
    copy: "Gujarati raab, rice and millet milk porridge, saffron-almond porridge and sprouted millet bowls — the recipes Indian families have trusted for generations, portioned for a child.",
    cta: { label: "Explore breakfast", to: "/meals?tab=breakfast" },
    images: [media.breakfast.raab, media.breakfast.bajraKadha, media.breakfast.saffronAlmond],
    badges: ["Traditional recipes", "Organic jaggery", "Warm and easy to digest", "Ready to eat"],
  },
  {
    id: "lunch",
    eyebrow: "Our lunches",
    title: "Fifteen lunches, planned so no week repeats",
    copy: "Varan bhaat, lauki dal, khichdi with kadhi, moringa sambar and puran poli. Gentle spice, real ghee, and a grain, a lentil and a vegetable on every plate.",
    cta: { label: "Explore lunch", to: "/meals?tab=week-1" },
    images: [media.meals.varanBhaat, media.meals.laukiDal, media.meals.moringaSambar],
    badges: ["Three-week rotation", "Gentle Indian flavours", "Age-based portions", "Cooked in A2 ghee"],
  },
  {
    id: "notes",
    eyebrow: "Our notes",
    title: "A note for you, and a note for them",
    copy: "Every meal travels with the ingredients and nutrition written plainly for you, and a fun line for your child explaining what the food does for their body.",
    cta: { label: "See our standards", to: "/food-standards" },
    images: [media.deliveryPackaging, media.ingredients, media.kitchenPrep],
    badges: ["Full ingredient list", "Allergens flagged", "Storage + reheating", "Plastic-free"],
  },
];

const HeroBlock = () => {
  const brand = useBrand();
  return (
    <section className="relative overflow-hidden bg-cream" data-testid="hero">
      {/* Coral panel with an organic hand-cut edge — the brand book cover motif. */}
      <div aria-hidden="true" className="absolute inset-y-0 right-0 hidden w-[52%] lg:block">
        <svg
          viewBox="0 0 600 800"
          preserveAspectRatio="none"
          className="h-full w-full"
          focusable="false"
        >
          <path
            d="M148 0C64 104 206 196 108 316C22 420 196 486 96 606C40 674 84 744 132 800H600V0H148Z"
            fill="#F05658"
          />
        </svg>
      </div>

      <div className="mbm-container relative py-14 sm:py-16 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr]">
          <Reveal>
            <span
              className="inline-flex items-center gap-2.5 rounded-full bg-surface px-4 py-2 shadow-soft"
              data-testid="hero-eyebrow"
            >
              <span aria-hidden="true" className="mbm-gradient h-2.5 w-7 rounded-full" />
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-coralInk">
                Now delivering in Pune
              </span>
            </span>
            <h1 className="mt-6 font-heading text-[2.2rem] font-bold leading-[1.06] tracking-[-0.015em] text-cocoa sm:text-[2.9rem] lg:text-[3.5rem]">
              <span className="block">A fresh take on</span>
              <span className="mt-1 inline-block">
                <WordRotator
                  className="italic text-coralInk"
                  words={["breakfast.", "lunch.", "school days.", "busy mornings."]}
                />
              </span>
            </h1>
            <p className="mt-6 max-w-md text-base font-medium leading-relaxed text-cocoa/75">
              Fresh, familiar Indian meals for growing children — cooked to a three-week menu,
              portioned by age, and delivered to your home or your child's school.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <CTAButton to="/plans" data-testid="hero-primary-cta">
                Get started
              </CTAButton>
              <CTAButton to="/meals" variant="outline" data-testid="hero-secondary-cta">
                See all our meals
              </CTAButton>
            </div>
            <dl className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4">
              {[
                [`${breakfastItems.length}`, "breakfasts"],
                ["15", "lunches"],
                ["4", "age bands"],
              ].map(([n, label]) => (
                <div key={label}>
                  <dt className="sr-only">{label}</dt>
                  <dd>
                    <span className="font-heading text-3xl font-bold text-coralInk">{n}</span>
                    <span className="ml-2 text-[13px] font-semibold uppercase tracking-[0.14em] text-cocoa/75">
                      {label}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={120} className="relative">
            <div
              aria-hidden="true"
              className="mbm-petal absolute -right-6 -top-8 h-32 w-32 bg-forest lg:hidden"
            />
            <div className="mbm-petal relative overflow-hidden border-[10px] border-surface shadow-softLg">
              <img
                src={media.motherAndChildKitchen}
                alt="A mother and her young daughter preparing a meal together in a bright kitchen"
                loading="eager"
                className="h-[21rem] w-full object-cover sm:h-[26rem] lg:h-[31rem]"
              />
            </div>
            <div
              className="mbm-scallop absolute -bottom-7 left-1 flex sm:-left-5 h-28 w-28 items-center justify-center text-center sm:h-32 sm:w-32"
              style={{ background: "var(--brand-amber)" }}
            >
              <p className="px-4 font-heading text-[13px] font-semibold italic leading-tight text-cocoa sm:text-sm">
                Made with love in {brand.serviceAreas.cities[0]}
              </p>
            </div>
            <div className="absolute -right-3 top-8 hidden flex-col gap-2 lg:flex">
              {["#E5097F", "#F0252A", "#F68825"].map((c) => (
                <span
                  key={c}
                  aria-hidden="true"
                  className="h-3.5 w-3.5 rounded-full ring-2 ring-white"
                  style={{ background: c }}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
      <div aria-hidden="true" className="mbm-squiggle" />
    </section>
  );
};

const ageBands = [
  {
    id: "2-4",
    badge: "2 to 4",
    title: "Gentle beginnings",
    copy: "Softer textures and smaller portions for toddlers and the early nursery years.",
  },
  {
    id: "5-7",
    badge: "5 to 7",
    title: "School mornings",
    copy: "Enough variety and energy to carry a child through a full school day of play.",
  },
  {
    id: "8-10",
    badge: "8 to 10",
    title: "Growing appetites",
    copy: "More grain, more lentil, more vegetable — portioned for a hungrier child.",
  },
  {
    id: "11-plus",
    badge: "11+",
    title: "Pre-teen plates",
    copy: "Substantial, balanced plates for pre-teens and young sportspeople.",
  },
];

const AgeBands = () => (
  <section className="relative overflow-hidden bg-surface py-16 sm:py-20" data-testid="age-bands">
    <div className="mbm-container">
      <Reveal className="max-w-2xl">
        <p className="mbm-eyebrow">Portioned by age</p>
        <h2 className="mt-4 font-heading text-[1.9rem] font-bold leading-[1.1] tracking-[-0.015em] sm:text-[2.4rem]">
          The same care, measured for four different ages.
        </h2>
        <p className="mt-4 max-w-xl text-sm font-medium leading-relaxed text-cocoa/70 sm:text-base">
          A two-year-old and an eleven-year-old do not need the same plate. You choose your child's
          band once and every meal arrives measured for them.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {ageBands.map((b, i) => (
          <Reveal key={b.id} delay={i * 80}>
            <div className="mbm-card mbm-card-hover h-full p-7" data-testid={`age-band-${b.id}`}>
              <span className="inline-flex items-center rounded-full bg-sageSoft px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-coralInk">
                Ages {b.badge}
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold tracking-[-0.01em]">{b.title}</h3>
              <p className="mt-3 text-sm font-medium leading-relaxed text-cocoa/70">{b.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CollectionSelector = () => {
  const [filter, setFilter] = useState("all");
  const shown = collections.filter((c) => filter === "all" || c.filter === filter);

  return (
    <section className="bg-cream py-20 sm:py-24" data-testid="collection-selector">
      <div className="mbm-container">
        <Reveal className="text-center">
          <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
            Let's find your child's perfect fit.
          </h2>
          <p className="mt-4 text-sm font-semibold text-cocoa/65 sm:text-base">
            Explore what we cook, and how you can subscribe.
          </p>
          <div className="mt-8 inline-flex flex-wrap justify-center gap-1.5 rounded-full bg-sageSoft p-1.5">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`collection-filter-${f.id}`}
                aria-pressed={filter === f.id}
                className={`min-h-[44px] rounded-full px-6 text-sm font-extrabold transition-all duration-300 ${
                  filter === f.id ? "bg-surface text-cocoa shadow-soft" : "text-cocoa/75 hover:text-cocoa"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c, i) => (
            <Reveal key={c.id} delay={i * 70}>
              <Link
                to={c.to}
                data-testid={`collection-tile-${c.id}`}
                className="mbm-card mbm-card-hover group flex h-full flex-col overflow-hidden"
              >
                <div className="relative overflow-hidden bg-creamDeep">
                  <img
                    src={c.image}
                    alt={c.title}
                    loading={i < 3 ? "eager" : "lazy"}
                    className="aspect-[5/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-cream/95 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-cocoa backdrop-blur">
                    {c.stage}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-heading text-2xl font-black tracking-[-0.02em]">{c.title}</h3>
                  <p className="mt-1 text-sm font-extrabold text-coralInk">{c.subtitle}</p>
                  <p className="mt-3 flex-1 text-sm font-medium leading-relaxed text-cocoa/70">
                    {c.copy}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-extrabold text-cocoa">
                    Explore
                    <ArrowRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const TransparencyBlock = () => (
  <section className="relative overflow-hidden bg-sageSoft py-20 sm:py-24" data-testid="transparency">
    <div className="mbm-container grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
      <Reveal>
        <span
          className="mbm-scallop inline-flex h-28 w-28 items-center justify-center text-center"
          style={{ background: "var(--brand-amber)" }}
        >
          <span className="px-5 font-heading text-[13px] font-black uppercase leading-tight text-cocoa">
            Always written down
          </span>
        </span>
        <h2 className="mt-8 font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          You deserve to know exactly what your child is eating.
        </h2>
        <p className="mt-5 max-w-xl text-sm font-medium leading-relaxed text-cocoa/70 sm:text-base">
          Every dish lists its ingredients, its allergens, its approximate nutrition and how to store
          and reheat it. Where a number is still being confirmed, we say so instead of estimating.
        </p>
        <div className="mt-8">
          <CTAButton to="/food-standards" variant="secondary" data-testid="transparency-cta">
            Read our food standards
          </CTAButton>
        </div>
      </Reveal>

      <div className="grid gap-4">
        {[
          {
            icon: ShieldCheck,
            title: "Always tested, always transparent",
            copy: "Pest-free, chemical-free and fertiliser-free staples, with supplier documentation kept on file.",
          },
          {
            icon: Sparkles,
            title: "Grounded in tradition, backed by experts",
            copy: "Recipes built on regional Indian home cooking and developed with paediatric nutritionists.",
          },
          {
            icon: ScrollText,
            title: "Balanced for a growing child",
            copy: "A grain, a lentil and a vegetable on every plate, with portions for ages 2–4, 5–7, 8–10 and 11+.",
          },
        ].map((c, i) => (
          <Reveal key={c.title} delay={i * 90}>
            <div className="mbm-card flex gap-4 p-6">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-amber text-cocoa">
                <c.icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-heading text-lg font-black leading-snug">{c.title}</h3>
                <p className="mt-1.5 text-sm font-medium leading-relaxed text-cocoa/70">{c.copy}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const NoListBlock = () => (
  <section className="relative overflow-hidden bg-creamDeep py-20 sm:py-24" data-testid="no-list">
    <div className="mbm-container text-center">
      <Reveal>
        <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          All the right no's,
          <br />
          <span className="text-coralInk">so you can say yes.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-sm font-medium leading-relaxed text-cocoa/70 sm:text-base">
          A short list of things you will never find in a Mothers by Mothers meal. Not because it
          sounds good, but because we would not feed them to our own children.
        </p>
      </Reveal>
    </div>
    <div className="mt-10 space-y-1">
      <NoMarquee items={noList} />
      <NoMarquee items={[...noList].reverse()} reverse />
    </div>
    <div className="mbm-container mt-10 text-center">
      <CTAButton to="/food-standards" variant="outline" data-testid="no-list-cta">
        See what we do use
      </CTAButton>
    </div>
  </section>
);

const HowItWorksBlock = () => (
  <section className="relative overflow-hidden bg-cream py-20 sm:py-24" data-testid="how-it-works-home">
    <div className="mbm-container grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
      <Reveal>
        <div className="mbm-tilt overflow-hidden rounded-[34px] shadow-softLg">
          <img
            src={media.deliveryPackaging}
            alt="Plastic-free packaging being prepared for delivery"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
            How it works
          </h2>
        </Reveal>
        <ol className="mt-8 space-y-6">
          {howItWorksDetailed.slice(0, 3).map((s, i) => (
            <Reveal as="li" key={s.title} delay={i * 90}>
              <div className="flex gap-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber font-heading text-xl font-black text-cocoa">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-heading text-xl font-black leading-snug">{s.title}</h3>
                  <p className="mt-1.5 text-sm font-medium leading-relaxed text-cocoa/70">{s.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={280} className="mt-9">
          <CTAButton to="/how-it-works" data-testid="how-it-works-cta">
            See the full process
          </CTAButton>
        </Reveal>
      </div>
    </div>
  </section>
);

const FounderQuotesBlock = () => (
  <section className="bg-forest py-20 sm:py-24" data-testid="founder-quotes">
    <div className="mbm-container">
      <Reveal className="max-w-2xl">
        <p className="mbm-eyebrow" style={{ color: "#FFFFFF" }}>
          Why this exists
        </p>
        <h2 className="mt-5 font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] text-white sm:text-[2.6rem]">
          Built by a mother, driven by love, backed by science.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {[
          "On the days you are sick, exhausted, or can barely pull yourself out of bed, one thing does not change: your child still needs to eat.",
          "Delivery apps were never a real option. Hidden sugars, preservatives and questionable ingredients make them unsafe for a growing child's daily diet.",
          "It takes a village to raise a child. Today's world left mothers to do it alone. We are here to be your village.",
        ].map((q, i) => (
          <Reveal key={q.slice(0, 20)} delay={i * 90}>
            <blockquote className="flex h-full flex-col justify-between rounded-card bg-white p-6 shadow-soft">
              <p className="font-heading text-lg font-bold leading-snug text-cocoa">“{q}”</p>
              <footer className="mt-6 text-[12px] font-extrabold uppercase tracking-[0.14em] text-coralInk">
                UV · Founder
              </footer>
            </blockquote>
          </Reveal>
        ))}
      </div>
      <Reveal delay={280} className="mt-10">
        <CTAButton to="/our-story" variant="onDark" data-testid="founder-quotes-cta">
          Read UV's story
        </CTAButton>
      </Reveal>
    </div>
  </section>
);

const DifferentBlock = () => (
  <section className="relative overflow-hidden bg-cream py-20 sm:py-24" data-testid="what-makes-different">
    <div className="mbm-container">
      <Reveal className="text-center">
        <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          What makes us
          <br />
          <span className="text-coralInk">different?</span>
        </h2>
      </Reveal>

      <div className="mt-14 space-y-16">
        {differentiators.map((d, i) => (
          <Reveal key={d.id}>
            <div
              className={`grid items-center gap-10 lg:grid-cols-2 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="relative mx-auto w-full max-w-[440px]">
                <div className="mbm-tilt overflow-hidden rounded-[32px] shadow-softLg">
                  <img
                    src={d.images[0]}
                    alt={d.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {d.images.slice(1).map((src, n) => (
                    <div
                      key={src}
                      className={`overflow-hidden rounded-[24px] shadow-soft ${
                        n === 0 ? "mbm-tilt-r" : ""
                      }`}
                    >
                      <img
                        src={src}
                        alt=""
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mbm-eyebrow">{d.eyebrow}</p>
                <h3 className="mt-4 font-heading text-2xl font-black leading-[1.06] tracking-[-0.02em] sm:text-[2rem]">
                  {d.title}
                </h3>
                <p className="mt-4 text-sm font-medium leading-relaxed text-cocoa/70 sm:text-base">
                  {d.copy}
                </p>
                <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                  {d.badges.map((b) => (
                    <li key={b} className="flex items-center gap-2.5">
                      <span className="mbm-scallop flex h-11 w-11 shrink-0 items-center justify-center">
                        <Check className="h-4 w-4 text-cocoa" aria-hidden="true" />
                      </span>
                      <span className="text-[13px] font-extrabold leading-tight text-cocoa">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <CTAButton
                    to={d.cta.to}
                    variant="secondary"
                    data-testid={`different-cta-${d.id}`}
                  >
                    {d.cta.label}
                  </CTAButton>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PlanStrip = () => {
  const plans = allPlans().filter((p) => p.durationId === "monthly");
  return (
    <section className="relative overflow-hidden bg-sageSoft py-20 sm:py-24" data-testid="plan-strip">
      <div className="mbm-container">
        <Reveal className="text-center">
          <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
            Pick the meals. Pick the length.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm font-semibold text-cocoa/65 sm:text-base">
            Breakfast, lunch, or both — for a single week or the full three-week cycle.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <Link
                to={`/checkout?plan=${p.id}`}
                data-testid={`home-plan-${p.id}`}
                className="mbm-card mbm-card-hover flex h-full flex-col p-6 sm:p-7"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-amber text-cocoa">
                  {p.collection === "breakfast" ? (
                    <Sunrise className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <UtensilsCrossed className="h-5 w-5" aria-hidden="true" />
                  )}
                </span>
                <h3 className="mt-5 font-heading text-xl font-black tracking-[-0.01em]">
                  {p.typeLabel}
                </h3>
                <p className="mt-2 flex-1 text-sm font-medium leading-relaxed text-cocoa/70">
                  {p.summary}
                </p>
                <p className="mt-5 text-sm font-extrabold text-coralInk">
                  {p.totalMeals} meals · {p.weeks} weeks
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={260} className="mt-10 text-center">
          <CTAButton to="/plans" data-testid="plan-strip-cta">
            Compare all plans
          </CTAButton>
        </Reveal>
      </div>
    </section>
  );
};

const WeekPeek = () => (
    <section className="relative overflow-hidden bg-cream py-20 sm:py-24" data-testid="week-peek">
      <div className="mbm-container">
      <Reveal className="text-center">
        <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          Three weeks, three moods.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm font-semibold text-cocoa/65 sm:text-base">
          Tap a week to see all five lunches with ingredients, allergens and nutrition.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {menuWeeks.map((week, i) => (
          <Reveal key={week.number} delay={i * 80}>
            <Link
              to={`/meals?tab=week-${week.number}`}
              data-testid={`week-peek-${week.number}`}
              className="group relative block overflow-hidden rounded-card shadow-softLg"
            >
              <img
                src={week.meals[0].image}
                alt={week.title}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(46,38,38,0) 34%, rgba(46,38,38,0.88) 100%)",
                }}
              />
              <span className="absolute left-4 top-4 rounded-full bg-amber px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-cocoa">
                Week {week.number}
              </span>
              <div className="absolute bottom-5 left-5 right-5">
                <h3 className="font-heading text-xl font-black text-white">{week.title}</h3>
                <p className="mt-1.5 text-[12px] font-bold text-white">
                  {week.dailyRange.calories} · {week.dailyRange.protein} per day
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ReviewsBlock = () => (
  <section className="relative overflow-hidden bg-creamDeep py-20 sm:py-24" data-testid="reviews">
    <div className="mbm-container text-center">
      <Reveal>
        <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          Don't take it from us.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm font-medium leading-relaxed text-cocoa/70 sm:text-base">
          We only publish reviews from families who have actually eaten with us — and we will never
          invent them. As parents share their words, they will appear here.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {homeCopy.testimonials.map((t, i) => (
          <Reveal key={t.placeholder} delay={i * 90}>
            <div className="mbm-card flex h-full flex-col items-center justify-center gap-3 border-dashed p-8">
              <span className="text-2xl font-black tracking-[0.2em] text-amber">★★★★★</span>
              <p className="text-sm font-bold text-cocoa/75">{t.placeholder}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={300} className="mt-10">
        <CTAButton to="/get-started" data-testid="reviews-cta">
          Start your child’s plan
        </CTAButton>
      </Reveal>
    </div>
  </section>
);

const CommunityBlock = () => (
  <section className="bg-cream py-20 sm:py-24" data-testid="community">
    <div className="mbm-container">
      <Reveal className="max-w-2xl">
        <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          Life with Mothers by Mothers.
        </h2>
        <p className="mt-4 text-sm font-medium leading-relaxed text-cocoa/70 sm:text-base">
          The story behind the kitchen, the standards we hold, and the mothers we want to employ.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {[
          {
            image: media.founderAndChild,
            title: "The story behind the kitchen.",
            copy: "Why UV started this, in her own words — the questions, the guilt and the decision to build it.",
            cta: { label: "Read the story", to: "/our-story" },
          },
          {
            image: media.ingredients,
            title: "The standards we hold.",
            copy: "Ingredients, sourcing, packaging and what we will not publish until it is proven.",
            cta: { label: "See our standards", to: "/food-standards" },
          },
          {
            image: media.kitchenPrep,
            title: "Mothers supporting mothers.",
            copy: "Paid, meaningful work for women whose careers were interrupted by childcare.",
            cta: { label: "The wider vision", to: "/mothers-supporting-mothers" },
          },
        ].map((c, i) => (
          <Reveal key={c.title} delay={i * 90}>
            <article className="mbm-card mbm-card-hover flex h-full flex-col overflow-hidden">
              <img
                src={c.image}
                alt={c.title}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-heading text-xl font-black leading-snug">{c.title}</h3>
                <p className="mt-2.5 flex-1 text-sm font-medium leading-relaxed text-cocoa/70">
                  {c.copy}
                </p>
                <Link
                  to={c.cta.to}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-extrabold text-coralInk"
                  data-testid={`community-cta-${i}`}
                >
                  {c.cta.label}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FaqBlock = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative overflow-hidden bg-sageSoft py-20 sm:py-24" data-testid="home-faqs">
      <div className="mbm-container grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
        <Reveal>
          <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
            Your questions,
            <br />
            <span className="text-coralInk">answered.</span>
          </h2>
          <div className="mt-8">
            <CTAButton to="/faqs" variant="outline" data-testid="home-faq-cta">
              All frequently asked questions
            </CTAButton>
          </div>
        </Reveal>
        <div className="space-y-3">
          {faqs.slice(0, 6).map((f, i) => (
            <Reveal key={f.q} delay={i * 60}>
              <div className="overflow-hidden rounded-[24px] bg-surface shadow-soft">
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  aria-expanded={open === i}
                  data-testid={`home-faq-${i}`}
                  className="flex min-h-[64px] w-full items-center justify-between gap-4 px-6 py-4 text-left"
                >
                  <span className="font-heading text-base font-black leading-snug">{f.q}</span>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber text-cocoa">
                    {open === i ? (
                      <Minus className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    )}
                  </span>
                </button>
                {open === i && (
                  <p className="px-6 pb-6 text-sm font-medium leading-relaxed text-cocoa/70">
                    {f.a}
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const EarlyAccessBlock = () => (
  <section className="relative overflow-hidden bg-creamDeep py-20 sm:py-24" data-testid="home-early-access">
    <div className="mbm-container grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
      <Reveal>
        <h2 className="font-heading text-[2rem] font-black leading-[1.02] tracking-[-0.02em] sm:text-[2.6rem]">
          Start your child’s plan this week.
        </h2>
        <p className="mt-5 max-w-md text-sm font-semibold leading-relaxed text-cocoa/75 sm:text-base">
          Tell us your child’s age, your area and the meals you want. We confirm your pricing,
          delivery window and start date with you. No payment is taken on this site.
        </p>
      </Reveal>
      <Reveal delay={120}>
        <EarlyAccessForm />
      </Reveal>
    </div>
  </section>
);

const VillageBand = () => {
  const brand = useBrand();
  return (
    <section className="relative overflow-hidden bg-coralInk py-20 sm:py-24" data-testid="village-band">
      <div
        aria-hidden="true"
        className="mbm-petal pointer-events-none absolute -left-32 -top-24 h-[30rem] w-[30rem] bg-white/10"
      />
      <div className="mbm-container relative text-center">
        <Reveal>
          <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-white">
            {brand.tagline}
          </p>
          <h2 className="mx-auto mt-5 max-w-3xl font-heading text-[2rem] font-bold leading-[1.1] text-white sm:text-[2.7rem]">
            We&apos;re here to be your village.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm font-semibold leading-relaxed text-white sm:text-base">
            Fresh, nutritionally balanced meals made especially for children — so one more part of
            the day is handled, and handled properly.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <CTAButton to="/get-started" variant="secondary" data-testid="village-cta">
              Start your plan
            </CTAButton>
            <CTAButton to="/our-story" variant="outline" data-testid="village-story-cta">
              Read our story
            </CTAButton>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { icon: Sparkles, label: "Freshly made daily" },
            { icon: HeartHandshake, label: "Paediatric nutritionists" },
            { icon: ShieldCheck, label: "No preservatives" },
            { icon: Truck, label: "Safe, hygienic delivery" },
            { icon: Heart, label: "Built by a mother" },
          ].map((f, i) => (
            <Reveal key={f.label} delay={i * 70}>
              <div className="flex flex-col items-center gap-3" data-testid={`village-icon-${i}`}>
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/45">
                  <f.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </span>
                <p className="text-[13px] font-bold leading-snug text-white">{f.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <HeroBlock />
      <Marquee items={trustItems} tone="plum" testId="trust-marquee" />
      <AgeBands />
      <CollectionSelector />
      <TransparencyBlock />
      <NoListBlock />
      <PlanStrip />
      <WeekPeek />
      <HowItWorksBlock />
      <FounderQuotesBlock />
      <DifferentBlock />
      <ReviewsBlock />
      <CommunityBlock />
      <VillageBand />
      <Marquee items={trustItems} tone="cream" reverse testId="trust-marquee-2" />
      <FaqBlock />
      <EarlyAccessBlock />
    </>
  );
}
